import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import safeStorage from '@github-ui/safe-storage'
import {CodeIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Box, Button, TabNav} from '@primer/react'
import type React from 'react'
import {useCallback, useEffect, useState, type ReactNode} from 'react'

const safeLocalStorage = safeStorage('localStorage')

export interface CodeDropdownButtonProps {
  primary: boolean
  codespacesEnabled: boolean
  isEnterprise: boolean
  localTab: ReactNode
  copilotTab: ReactNode
  codespacesTab: ReactNode
}

enum ActiveTab {
  Local = 'local',
  Codespaces = 'cloud',
  Copilot = 'copilot',
}

export function CodeDropdownButton(props: CodeDropdownButtonProps) {
  const {primary, codespacesEnabled, isEnterprise, localTab, copilotTab, codespacesTab} = props
  const localStorageDefaultTabKey = 'code-button-default-tab'
  const [activeTab, setActiveTab] = useState<string>(ActiveTab.Local)

  const onCodespacesTabClick = useCallback((ev?: React.MouseEvent) => {
    setActiveTab(ActiveTab.Codespaces)
    safeLocalStorage.setItem(localStorageDefaultTabKey, ActiveTab.Codespaces)
    ev?.preventDefault()
  }, [])

  const onLocalTabClick = useCallback((ev?: React.MouseEvent) => {
    setActiveTab(ActiveTab.Local)
    safeLocalStorage.setItem(localStorageDefaultTabKey, ActiveTab.Local)
    ev?.preventDefault()
  }, [])

  const onCopilotTabClick = useCallback((ev?: React.MouseEvent) => {
    setActiveTab(ActiveTab.Copilot)
    safeLocalStorage.setItem(localStorageDefaultTabKey, ActiveTab.Copilot)
    ev?.preventDefault()
  }, [])

  useEffect(() => {
    const defaultActiveTab = safeLocalStorage.getItem(localStorageDefaultTabKey)
    if (defaultActiveTab === ActiveTab.Codespaces && codespacesEnabled) {
      onCodespacesTabClick()
    }
    // Only run after the initial render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const tabLinkStyles = {
    height: '40px',
    width: '50%',
    borderBottomRightRadius: 0,
    borderBottomLeftRadius: 0,
    borderTop: 0,
    color: 'fg.muted',
    ':hover': {
      backgroundColor: 'unset',
      borderColor: 'border.default',
    },
    '&.selected': {
      backgroundColor: 'canvas.overlay',
    },
    ':not(&.selected)': {
      border: 0,
    },
  }

  const copilotWorkspaceEnabled = useFeatureFlag('copilot_workspace')

  return (
    <ActionMenu>
      <ActionMenu.Button
        variant={primary ? 'primary' : undefined}
        sx={{
          svg: primary ? {color: 'fg.primary'} : undefined,
        }}
        leadingVisual={CodeButtonIcon}
      >
        Code
      </ActionMenu.Button>
      <ActionMenu.Overlay width="auto" align="end">
        <ActionList className={'react-overview-code-button-action-list'} sx={{py: 0}}>
          {!isEnterprise && codespacesEnabled && (
            <TabNav>
              <TabNav.Link
                as={Button}
                selected={activeTab === ActiveTab.Local}
                onClick={onLocalTabClick}
                sx={{
                  ...tabLinkStyles,
                  borderLeft: 0,
                }}
              >
                Local
              </TabNav.Link>
              <TabNav.Link
                as={Button}
                selected={activeTab === ActiveTab.Codespaces}
                onClick={onCodespacesTabClick}
                sx={{
                  ...tabLinkStyles,
                  borderRight: copilotWorkspaceEnabled ? null : 0,
                }}
              >
                Codespaces
              </TabNav.Link>
              {copilotWorkspaceEnabled && (
                <TabNav.Link
                  as={Button}
                  selected={activeTab === ActiveTab.Copilot}
                  onClick={onCopilotTabClick}
                  sx={{
                    ...tabLinkStyles,
                    borderRight: 0,
                  }}
                >
                  Copilot
                </TabNav.Link>
              )}
            </TabNav>
          )}
          {activeTab === 'local' && localTab}
          {activeTab === 'cloud' && codespacesEnabled && codespacesTab}
          {activeTab === ActiveTab.Copilot && copilotWorkspaceEnabled && copilotTab}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

function CodeButtonIcon() {
  return (
    <Box sx={{'@media screen and (max-width: 544px)': {display: 'none'}}}>
      <CodeIcon />
    </Box>
  )
}

try{ CodeDropdownButton.displayName ||= 'CodeDropdownButton' } catch {}
try{ CodeButtonIcon.displayName ||= 'CodeButtonIcon' } catch {}